import React, { useEffect, useRef, useState } from 'react';
import { CloseIcon } from './icons/CloseIcon';

export const VideoPreviewModal = ({ videoUrl, trimStart, trimEnd, onClose }) => {
    const [objectUrl, setObjectUrl] = useState(null);
    const videoRef = useRef(null);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    useEffect(() => {
        let isMounted = true;
        let tempUrl = null;

        if (videoUrl && videoUrl.startsWith('data:')) {
            fetch(videoUrl)
                .then(res => res.blob())
                .then(blob => {
                    if (isMounted) {
                        tempUrl = URL.createObjectURL(blob);
                        setObjectUrl(tempUrl);
                    }
                })
                .catch(error => {
                    console.error("Error converting data URL to blob URL:", error);
                    if (isMounted) {
                        setObjectUrl(videoUrl);
                    }
                });
        } else {
            setObjectUrl(videoUrl);
        }

        return () => {
            isMounted = false;
            if (tempUrl) {
                URL.revokeObjectURL(tempUrl);
            }
        };
    }, [videoUrl]);

    // Nov 2026 — Honor metadata trim (`trimStart` / `trimEnd`) if the shot
    // has a saved trim range. Playback starts at trimStart and loops back
    // to trimStart when it reaches trimEnd, so the preview shows exactly
    // what the user chose to keep.
    useEffect(() => {
        const v = videoRef.current;
        if (!v || !objectUrl) return;

        const hasTrim = typeof trimStart === 'number' || typeof trimEnd === 'number';
        if (!hasTrim) return;

        const start = typeof trimStart === 'number' ? trimStart : 0;

        const onLoaded = () => { v.currentTime = start; };
        const onTimeUpdate = () => {
            const end = typeof trimEnd === 'number' ? trimEnd : v.duration;
            if (v.currentTime >= end - 0.05) v.currentTime = start;
            else if (v.currentTime < start - 0.05) v.currentTime = start;
        };

        v.addEventListener('loadedmetadata', onLoaded);
        v.addEventListener('timeupdate', onTimeUpdate);
        // If metadata is already ready (fast cache), seek immediately.
        if (!Number.isNaN(v.duration) && v.duration > 0) v.currentTime = start;

        return () => {
            v.removeEventListener('loadedmetadata', onLoaded);
            v.removeEventListener('timeupdate', onTimeUpdate);
        };
    }, [objectUrl, trimStart, trimEnd]);

    const hasTrim = typeof trimStart === 'number' || typeof trimEnd === 'number';
    const trimSpan = hasTrim
        ? `${(trimStart ?? 0).toFixed(1)}s → ${(trimEnd ?? 0).toFixed(1)}s`
        : null;

    return (
        <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
        >
            <div
                className="bg-[#181820] border border-gray-700 rounded-2xl w-full max-w-4xl h-auto flex flex-col overflow-hidden relative"
                onClick={e => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute top-2 right-2 p-2 rounded-full bg-black/50 hover:bg-gray-700 z-10"
                    aria-label="Close video preview"
                >
                    <CloseIcon className="w-6 h-6 text-white" />
                </button>
                {hasTrim && (
                    <div className="absolute top-2 left-2 z-10 bg-purple-600/90 text-white text-[10px] font-medium px-2 py-1 rounded-full shadow-lg" data-testid="video-preview-trim-badge">
                        ✂ Trimmed · {trimSpan}
                    </div>
                )}
                {objectUrl ? (
                    <video
                        ref={videoRef}
                        src={objectUrl}
                        controls
                        autoPlay
                        loop
                        className="w-full max-h-[85vh] object-contain"
                    >
                        Your browser does not support the video tag.
                    </video>
                ) : (
                    <div className="w-full max-h-[85vh] aspect-video flex items-center justify-center">
                        <p className="text-gray-400">Loading video...</p>
                    </div>
                )}
            </div>
        </div>
    );
};
